import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Eyebrow from "@/components/Eyebrow";
import SEO from "@/components/SEO";
import FavoriteButton from "@/components/FavoriteButton";
import { PROGRAMS, PROGRAM_COLORS, PROGRAM_LABELS } from "@/data/programs";
import { Heart, Loader2 } from "lucide-react";

/**
 * Public, read-only view of a supporter's favorite programs (shared via link).
 */
export default function SharedPrograms() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/shared-programs/${shareId}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [shareId]);

  const saved = data ? PROGRAMS.filter((p) => data.favorites.includes(p.slug)) : [];

  return (
    <section className="pt-24 pb-16 min-h-[60vh]">
      <SEO title="Shared program picks" description="A HOFA supporter's favorite programs across the ERSEH framework." path={`/shared/${shareId}`} />
      <div className="max-w-[1140px] mx-auto px-6">
        {!data && !notFound && (
          <div className="flex items-center gap-2 text-ink/60 py-16 justify-center">
            <Loader2 className="animate-spin" size={20} /> Loading shared list…
          </div>
        )}

        {notFound && (
          <div className="border border-dashed border-ink/20 rounded-[16px] p-10 text-center max-w-[560px]" data-testid="shared-programs-not-found">
            <p className="text-ink/75 mb-6">This shared list is no longer available.</p>
            <Link to="/programs" className="inline-block bg-palm text-white font-bold rounded-full px-6 py-3 hover:shadow-xl transition no-underline" data-testid="shared-programs-browse-link">
              Browse HOFA programs
            </Link>
          </div>
        )}

        {data && (
          <>
            <Eyebrow>Shared with you</Eyebrow>
            <h1 className="text-[clamp(34px,4.5vw,52px)] mb-4 max-w-[24ch] font-display font-bold" data-testid="shared-programs-title">
              {data.owner}&rsquo;s HOFA program picks.
            </h1>
            <p className="text-ink/75 max-w-[62ch] mb-10">
              A friend of HOFA saved these programs and wanted you to see them. Tap the heart to keep your own list.
            </p>

            {saved.length === 0 && (
              <div className="border border-dashed border-ink/20 rounded-[16px] p-10 text-center max-w-[560px]" data-testid="shared-programs-empty">
                <p className="text-ink/75 mb-6">This list is empty right now — but there's plenty to explore.</p>
                <Link to="/programs" className="inline-block bg-palm text-white font-bold rounded-full px-6 py-3 hover:shadow-xl transition no-underline">
                  Browse programs
                </Link>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shared-programs-grid">
              {saved.map((p) => (
                <div key={p.slug} className="relative">
                  <Link
                    to={`/programs/${p.slug}`}
                    data-testid={`shared-program-card-${p.slug}`}
                    className="bg-white border border-ink/10 rounded-[14px] overflow-hidden hover:-translate-y-1 hover:shadow-xl transition no-underline text-ink flex flex-col h-full"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-ink/5">
                      <img src={p.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-6 flex flex-col gap-2.5 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[11.5px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white"
                          style={{ background: PROGRAM_COLORS[p.key] }}
                        >
                          {PROGRAM_LABELS[p.key]}
                        </span>
                        {p.isNew && (
                          <span className="text-[11.5px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full bg-mango text-palm-deep">New</span>
                        )}
                      </div>
                      <h3 className="text-lg font-display">{p.title}</h3>
                      {p.full && <p className="text-sm font-semibold text-ink/60">{p.full}</p>}
                      <p className="text-[15px] text-ink/75 flex-1">{p.tagline}</p>
                      <span className="text-palm font-bold text-sm mt-2">Read more →</span>
                    </div>
                  </Link>
                  <FavoriteButton slug={p.slug} className="absolute top-3 right-3 z-10" />
                </div>
              ))}
            </div>

            <div className="mt-14 text-center border-t border-ink/10 pt-10">
              <Heart className="mx-auto text-berry mb-3" size={24} />
              <p className="text-ink/75 mb-5">Inspired? Explore everything HOFA runs in District 17.</p>
              <Link to="/programs" data-testid="shared-programs-cta" className="inline-block bg-mango text-palm-deep font-bold rounded-full px-7 py-3.5 hover:shadow-xl hover:-translate-y-0.5 transition no-underline">
                Explore all programs
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
